import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {
  Box,
  CircularProgress,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { api } from '../api/client'

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const whole = Math.floor(seconds)
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

interface Props {
  jobId: string
  fileId: string
  /** False until the server has built the MP3; the first play builds it. */
  ready: boolean
}

/**
 * Plays the MP3 rather than the stored WAV.
 *
 * Browsers handle WAV perfectly well, so this is about size: 8 MB against 64 MB
 * to start hearing a take. The server supports range requests, which is what
 * makes the scrubber able to seek instead of refetching the whole thing.
 */
export default function FilePlayer({ jobId, fileId, ready }: Props) {
  const audio = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [started, setStarted] = useState(false)
  const [wantPlay, setWantPlay] = useState(false)

  // Play from an effect, never from the click handler. `src` is derived from
  // `started`, so calling play() in the same tick that sets it plays an element
  // that still has no source: the promise rejects and the first click of every
  // preview reports a failure. By the time an effect runs, React has committed.
  useEffect(() => {
    if (!wantPlay) return
    setWantPlay(false)
    const element = audio.current
    if (!element) return
    element.play().catch(() => {
      setError('Could not play this file.')
      setLoading(false)
    })
  }, [wantPlay])

  useEffect(() => {
    const element = audio.current
    return () => {
      // Stop the audio if the row is collapsed or the page changes, rather
      // than leaving a track playing from something no longer on screen.
      element?.pause()
    }
  }, [])

  function toggle() {
    const element = audio.current
    if (!element) return

    if (playing) {
      element.pause()
      return
    }

    setError(null)
    // The very first play may have to wait for the server to encode, which
    // takes a few seconds and then never happens again for this file.
    if (!started && !ready) setLoading(true)
    setStarted(true)
    setWantPlay(true)
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title={playing ? 'Pause' : 'Preview'}>
          <span>
            <IconButton size="small" onClick={toggle} disabled={loading}>
              {loading ? (
                <CircularProgress size={18} />
              ) : playing ? (
                <PauseIcon fontSize="small" />
              ) : (
                <PlayArrowIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {started && !error && (
          <>
            <Slider
              size="small"
              min={0}
              max={duration || 1}
              value={position}
              onChange={(_event, value) => {
                const element = audio.current
                if (element) element.currentTime = value as number
              }}
              sx={{ minWidth: 120, maxWidth: 240 }}
              aria-label="Seek"
            />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {clock(position)} / {clock(duration)}
            </Typography>
          </>
        )}

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Stack>

      <audio
        ref={audio}
        // Nothing is fetched until play is pressed. Preloading a whole page of
        // results would pull megabytes nobody asked for.
        preload="none"
        src={started ? api.previewUrl(jobId, fileId) : undefined}
        onPlay={() => {
          setPlaying(true)
          setLoading(false)
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false)
          setPosition(0)
        }}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
        onError={() => {
          setLoading(false)
          setPlaying(false)
          setError('No preview available.')
        }}
      />
    </Box>
  )
}
